import { useMemo } from 'react';

import type { AxiosInstance } from 'axios';
import axios from 'axios';

import { getAppInsights } from 'analytics/appInsights';
import { getAccessToken } from 'api/authentication/utils/getAccessToken';
import type { OutputOperationResult } from 'api/types';
import { ENV_VARS } from 'app/config/environment-vars';
import type { EventHistoryInfo } from 'pages/projects/projectPage/selectedProject/projectHistory/EventHistory.types';
import type { PropertyValue, QueryInitialization } from 'state/data/dataState';
import type { AccessRightsMap } from 'state/grids/gridState';
import { HTTP_STORE } from 'state/http/httpStore';
import type { EPropertyTypes, HyperRelation, Template } from 'state/selectedTemplate/selectedTemplateStore';
import type { DataNodeDeletedEvent, RelationshipsChangedEvent } from 'state/signalr/signalrState';
import type { IDictionary, SemEdge, SemNode } from 'types/data/semNode';

import type { DefaultCasedOutputOperationResult } from '../outputOperationResult';
import type { ReportRun } from './reports/reportModels';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** When true, DataApiClient skips HTTP_STORE / AvailableService tracking. */
    skipAvailabilityTracking?: boolean;
  }
}

export const useDataApiClient = () => {
  return useMemo(() => {
    return new DataApiClient();
  }, []);
};

export class DataApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${ENV_VARS.DATA_URL}/api`,
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true
    });

    this.client.interceptors.request.use((config) => {
      if (!config.skipAvailabilityTracking) {
        HTTP_STORE.getState().start('data');
      }
      const token = getAccessToken();
      config.headers.Authorization = `Bearer ${token}`;

      return config;
    });

    this.client.interceptors.response.use(
      function (response) {
        if (!response.config.skipAvailabilityTracking) {
          HTTP_STORE.getState().stop('data');
        }

        // Retrieve the data from the response directly
        // So we don't need to call response.data later.
        return response.data;
      },
      function (error) {
        const appInsights = getAppInsights();
        appInsights.trackException({ exception: new Error(`DataApiClient ::: ${error.message}`) });

        if (!error.config?.skipAvailabilityTracking) {
          HTTP_STORE.getState().error('data', error);
        }

        return Promise.reject(error);
      }
    );
  }

  public GetProjects = async (): Promise<OutputOperationResult<ProjectNode[]>> => {
    return await this.client.get('projects', { skipAvailabilityTracking: true });
  };

  public GetUserSpecificTemplate = async (projectId: string): Promise<OutputOperationResult<UserSpecificTemplate>> => {
    return await this.client.get(`projects/${projectId}/template`);
  };

  public UnlockProjectFromCache = async (projectId: string): Promise<OutputOperationResult<boolean>> => {
    return await this.client.patch(`projects/${projectId}:unlock-cache`);
  };

  // ProjectDataIntegrityController
  public ValidateProjectData = async (
    projectId: string
  ): Promise<OutputOperationResult<DataIntegrityValidationResult>> => {
    return await this.client.get(`ProjectDataIntegrity/${projectId}`);
  };

  public ValidateHyperNodeStructures = async (
    projectId: string
  ): Promise<OutputOperationResult<InvalidHyperNodeContext[]>> => {
    return await this.client.get(`ProjectDataIntegrity/${projectId}/hypernodes`);
  };

  public RepairHyperNodeStructures = async (projectId: string): Promise<OutputOperationResult<unknown>> => {
    return await this.client.post(`ProjectDataIntegrity/${projectId}/hypernodes:merge`);
  };

  public RelabelRelations = async (
    projectId: string,
    context: RepairRelationContext
  ): Promise<OutputOperationResult<RepairRelationResult>> => {
    return await this.client.post(`ProjectDataIntegrity/${projectId}/relations/relabel`, context);
  };

  public RelabelNodes = async (
    projectId: string,
    context: RepairNodeContext
  ): Promise<OutputOperationResult<RepairRelationResult>> => {
    return await this.client.post(`ProjectDataIntegrity/${projectId}/nodes/relabel`, context);
  };

  // ProjectsController
  public ClearUserProjectCache = async (
    projectId: string,
    userEmail: string
  ): Promise<OutputOperationResult<boolean>> => {
    return await this.client.patch(`projects/${projectId}/users:clear-cache`, {
      userEmail
    });
  };

  public UpdateProjectTeam = async (
    projectId: string,
    projectTeamId: string
  ): Promise<OutputOperationResult<ProjectNode>> => {
    return await this.client.patch(`projects/${projectId}/projectTeam`, projectTeamId);
  };

  public CreateProject = async (model: CreateProjectModel): Promise<OutputOperationResult<ProjectNode>> => {
    return await this.client.post('projects', model, { skipAvailabilityTracking: true });
  };

  public CopyProject = async (
    projectId: string,
    model: CopyProjectRequest
  ): Promise<OutputOperationResult<ProjectNode>> => {
    return await this.client.post(`projects/${projectId}:copy`, model);
  };

  public GetEditRelationsGrid = async (
    projectId: string,
    queryId: string,
    queryNodeId: string,
    queryRelationId: string,
    parentDataNodeId: string,
    proposalId?: string
  ): Promise<OutputOperationResult<QueryInitialization>> => {
    return await this.client.get(`projects/${projectId}/queries/${queryId}/nodes/${queryNodeId}/data:schema`, {
      params: { queryRelationId, parentDataNodeId, proposalId }
    });
  };

  public SaveRelations = async (
    resultSetId: string,
    projectId: string,
    queryId: string,
    queryNodeId: string,
    rootDataNodeId: string,
    parentDataNodeId: string,
    brokenRelationIds: string[],
    nodesToConnectIds: string[],
    nodesToCreate: string[]
  ): Promise<RelationshipsChangedEvent> => {
    return await this.client.patch(`projects/${projectId}/queries/${queryId}/nodes/${queryNodeId}/data:options`, {
      resultSetId,
      rootDataNodeId,
      parentDataNodeId,
      brokenRelationships: brokenRelationIds,
      nodesToConnect: nodesToConnectIds,
      nodesToCreate
    } satisfies SaveRelationsModel);
  };

  public DeleteDataNode = async (
    projectId: string,
    templateNodeId: string,
    dataNodeId: string,
    queryNodeId: string,
    resultSetId: string,
    clearedNodes: { [key: string]: string[] },
    clearedRelationships: { [key: string]: string[] },
    parentRelationId?: string
  ): Promise<DataNodeDeletedEvent> => {
    return await this.client.post(`projects/${projectId}/nodes/${dataNodeId}:delete`, {
      parentRelationId,
      templateNodeId,
      queryNodeId,
      resultSetId,
      clearedNodes,
      clearedRelationships
    } satisfies DeleteNodeModel);
  };

  public GetNodeDecomposition = async (
    projectId: string,
    nodeId: string
  ): Promise<OutputOperationResult<TreeNode[]>> => {
    return await this.client.get(`projects/${projectId}/decompositions/${nodeId}`);
  };

  public MoveNodeInDecomposition = async (
    projectId: string,
    nodeId: string,
    dataNodeId: string,
    previousParentId: string | undefined,
    nextParentId: string | undefined
  ): Promise<IDataEventBase> => {
    return await this.client.patch(`projects/${projectId}/decompositions/${nodeId}/${dataNodeId}/move`, {
      nextParentId,
      previousParentId
    } satisfies MoveNodeInDecompositionModel);
  };

  public AddRootNodeToDecomposition = async (
    projectId: string,
    templateNodeId: string,
    propertyValues: IDictionary<string>
  ): Promise<DefaultCasedOutputOperationResult<SemNode>> => {
    return await this.client.post(`projects/${projectId}/decompositions/${templateNodeId}`, propertyValues);
  };

  public GetQueryValidationResult = async (templateId: string, queryId: string): Promise<QueryValidationResult> => {
    return await this.client.get(`validations/${templateId}/queries/${queryId}`);
  };
  public GetAllQueryValidationResults = async (templateId: string): Promise<QueryValidationResult[]> => {
    return await this.client.get(`validations/${templateId}`);
  };

  // Reporting
  public RunReport = async (
    projectId: string,
    reportId: string,
    fileType: string
  ): Promise<OutputOperationResult<unknown>> => {
    return await this.client.get(`projects/${projectId}/reports/${reportId}/run`, {
      params: { fileType: fileType }
    });
  };

  public GetJson = async (projectId: string, reportId: string): Promise<OutputOperationResult<string>> => {
    return await this.client.get(`projects/${projectId}/reports/${reportId}/json`);
  };

  public GetMarkers = async (templateId: string, reportId: string): Promise<OutputOperationResult<string>> => {
    return await this.client.get(`templates/${templateId}/reports/${reportId}/markers`);
  };

  public GetReports = async (projectId: string): Promise<DefaultCasedOutputOperationResult<ReportRun[]>> => {
    return await this.client.get(`projects/${projectId}/reports`);
  };

  public DownloadReport = async (
    projectId: string,
    reportId: string,
    reportRunId: string
  ): Promise<{ url: string }> => {
    return await this.client({
      url: `projects/${projectId}/reports/${reportId}/runs/${reportRunId}`,
      method: 'GET',
      responseType: 'blob',
      skipAvailabilityTracking: true
    }).then((response) => {
      const blob = response as unknown as Blob;
      const url = window.URL.createObjectURL(blob);
      return { url };
    });
  };

  public DeleteReport = async (
    projectId: string,
    reportId: string,
    reportRunId: string
  ): Promise<OutputOperationResult<unknown>> => {
    return await this.client.delete(`projects/${projectId}/reports/${reportId}/runs/${reportRunId}`);
  };

  // personal page
  public GetPersonalPageData = async (projectId: string): Promise<OutputOperationResult<PersonalPageDto>> => {
    return await this.client.get(`projects/${projectId}/personal-page`, { skipAvailabilityTracking: true });
  };

  // find and replace
  public FindAndReplace = async (
    projectId: string,
    propertyTraversalId: string,
    oldValue: PropertyValue,
    newValue: PropertyValue,
    involvedNodeIds: string[],
    proposalId?: string
  ): Promise<OutputOperationResult<unknown>> => {
    return await this.client.post(`projects/${projectId}:find-and-replace`, {
      propertyTraversalId,
      oldValue,
      newValue,
      involvedNodeIds,
      proposalId
    });
  };

  // project History Node property
  public GetNodePropertyProjectHistory = async (
    projectId: string,
    nodeId: string,
    propertyId: string
  ): Promise<OutputOperationResult<EventHistoryInfo[]>> => {
    return await this.client.get(`/projectHistory/project/${projectId}/node/${nodeId}/property/${propertyId}`);
  };
}

export interface CountResult {
  Count: number;
}

export interface ProjectNode {
  id: string;
  templateId: string;
  name: string;
  projectTeamId?: string | null;
  isLocked: boolean;
}

export interface DefaultCasedProjectNode {
  Id: string;
  TemplateId: string;
  Name: string;
  ProjectTeamId?: string | null;
  IsLocked: null;
}

export interface SaveRelationsModel {
  resultSetId: string;
  rootDataNodeId: string;
  parentDataNodeId: string;
  brokenRelationships: string[];
  nodesToConnect: string[];
  nodesToCreate: string[];
}

export interface CreateProjectModel {
  templateId: string;
  name: string;
  numberOfTestDataNodes: number;
  projectTeamId?: string;
  userRoleAssignments: { [key: string]: string[] };
}

export interface CopyProjectRequest {
  newProjectName: string;
}

export interface UpdatePropertyModel {
  value: any;
  propertyType: EPropertyTypes;
}

export interface PossibleSemNode {
  SemNode: SemNode;
  ParentEdge?: SemEdge;
}

interface DeleteNodeModel {
  templateNodeId: string;
  parentRelationId?: string;
  queryNodeId: string;
  resultSetId: string;
  clearedNodes: Record<string, string[]>;
  clearedRelationships: Record<string, string[]>;
}

export interface NodeDeletionResult {
  NodeToDelete: SemNode;

  /**Indicates if the given object is a node */
  IsNodeMap: { [key: string]: boolean };

  /**Map of the Data node to the DataDescription */
  NodeDescriptionMap: { [key: string]: string };

  /**Map of the Data edge to the RelationDescription */
  RelationDescriptionMap: { [key: string]: string };

  /**Description of the deletion of the given node, with the queryObjectId (node traversal or relation traversal) as the key */
  NodeDescriptions: { [key: string]: NodeDescription };

  /**Description of the deletion of the given edge, with the queryObjectId (node traversal or relation traversal) as the key */
  RelationDescriptions: { [key: string]: RelationDescription };

  NeedsConfirmation: boolean;
}

export interface RelationDescription {
  Id: string;
  IsBlocked: boolean;
  IsIntendedToBeDeleted: boolean;
  IsReverse: boolean;

  IsHyper: boolean;

  TemplateEdgeId: string;
}

export interface NodeDescription {
  Id: string;
  IsIntendedToBeDeleted: boolean;
  IsBlocked: boolean;

  IsHyper: boolean;

  TemplateNodeId: string;
}

/** Returned by the legacy GET /decompositions/{nodeId} endpoint (flat list, all at once) */
export interface TreeNode {
  id: string;
  parentId: string | null;
  title: string;
  subtitle: string;
}

export interface MoveNodeInDecompositionModel {
  previousParentId: string | undefined;
  nextParentId: string | undefined;
}

export interface IDataEventBase {
  Tenant: string;
  UserId: string;
  UserEmail: string;
  TemplateId: string;
  ProjectId: string;
  /**
   * Absent on live (non-proposal) events. The backend may serialize this as JSON `null` rather than
   * omitting it — don't assume `undefined`; normalize with `normalizeProposalId` before a strict
   * comparison or query key.
   */
  ProposalId?: string;
  On: Date | string;
  EventId: string;
}

export interface UpdateProjectModel {
  ProjectId: string;
  TargetTemplateId: string;
}

export interface UserSpecificTemplate {
  template: Template;

  nodeAccessRightsByRoleMap: AccessRightsMap;
  propertyAccessRightsByRoleMap: AccessRightsMap;
  relationAccessRightsByRoleMap: AccessRightsMap;
}

export interface QueryValidationResult {
  isValid: boolean;
  validationErrors: { [key: string]: string };
  queryId: string;
}

export interface PersonalPageDto {
  generatedOn: Date | undefined;
  numberOfItems: { [key: string]: number };
}

// data integrity
export interface DataIntegrityValidationResult {
  badLabels: string[];
  badRelations: BadRelation[];
  allowedLabels: AllowedLabel[];
}

export interface BadRelation {
  start: string;
  relationLabel: string;
  targetLabel: string;
  proposedRepairs: RepairRelationContext[];
}

export interface AllowedLabel {
  start: string;
  relations: { [key: string]: string[] };
}

export interface RepairRelationContext {
  projectId: string;
  templateId: string;
  startNodeLabel: string;
  currentRelationLabel: string;
  targetNodeLabel: string;
  desiredRelationLabel: string;
}

export interface RepairRelationResult {
  numberOfRelationsRenamed: number;
}

export interface InvalidHyperNodeContext {
  hyperRelation: HyperRelation;
  invalidStructures: InvalidHyperNodeStructure[];
  startNodeLabel: string;
  partOneRelationLabel: string;
  hyperNodeNodeLabel: string;
  partTwoRelationLabel: string;
  targetNodeLabel: string;
}

export interface InvalidHyperNodeStructure {
  startNode: SemNode;
  endNode: SemNode;
  hyperNodes: SemNode[];
  desiredHyperNode: SemNode;
}

export interface RepairNodeContext {
  projectId: string;
  templateId: string;
  desiredNodeLabel: string;
  currentNodeLabel: string;
}

// end of data integrity
